import { cn } from '@/lib/utils'
import { type ComponentProps, type ReactNode, useId } from 'react'
import { Switch as SwitchPrimitive } from './ui/switch'

/**
 * Evento sintético com o mesmo formato consumido pelos call sites que vieram
 * do `Switch` do MUI (`event.target.checked`). O Radix entrega só o boolean.
 */
export type SwitchChangeEvent = { target: { checked: boolean } }

export interface SwitchProps
    extends Omit<
        ComponentProps<typeof SwitchPrimitive>,
        'size' | 'onChange' | 'onCheckedChange'
    > {
    size?: 'small' | 'medium'
    onChange?: (event: SwitchChangeEvent, checked: boolean) => void
    label?: ReactNode
    /** Classe do wrapper que envolve switch e rótulo. Só vale com `label`. */
    containerClassName?: string
}

export function Switch({
    size = 'medium',
    onChange,
    className,
    label,
    containerClassName,
    ...props
}: SwitchProps) {
    const generatedId = useId()
    const id = props.id ?? generatedId

    const control = (
        <SwitchPrimitive
            {...props}
            id={id}
            size={size === 'small' ? 'sm' : 'default'}
            className={cn(
                'cursor-pointer disabled:cursor-not-allowed',
                className,
            )}
            onCheckedChange={checked =>
                onChange?.({ target: { checked } }, checked)
            }
        />
    )

    if (label === undefined) return control

    return (
        <span
            className={cn('inline-flex items-center gap-2', containerClassName)}
        >
            {control}
            <label
                htmlFor={props.disabled ? undefined : id}
                className={cn(
                    'select-none',
                    props.disabled
                        ? 'cursor-not-allowed opacity-50'
                        : 'cursor-pointer',
                )}
            >
                {label}
            </label>
        </span>
    )
}
